import React, { createContext, useState, useContext, useEffect } from 'react';
import { lightTheme } from '../styles/themes/light';
import { darkTheme } from '../styles/themes/dark';

/**
 * ColorModeContext - Context for managing light/dark theme
 * 
 * Properties:
 * - colorMode: Current theme mode ('light' or 'dark')
 * - toggleColorMode: Function to toggle between light and dark modes
 * - isDark: Boolean indicating if dark mode is active
 */
const ColorModeContext = createContext({
  colorMode: 'light',
  toggleColorMode: () => {},
  isDark: false,
});

export const ColorModeProvider = ({ children }) => {
  // Get the initial color mode preference from localStorage or system preference
  const getInitialColorMode = () => {
    // Check if we have a stored preference
    const savedColorMode = localStorage.getItem('color-mode');
    if (savedColorMode === 'light' || savedColorMode === 'dark') {
      return savedColorMode;
    }
    
    // Check if user has dark mode preference in their system
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    
    // Default to light mode
    return 'light';
  };

  const [colorMode, setColorMode] = useState(getInitialColorMode);
  const isDark = colorMode === 'dark';

  // Function to toggle between light and dark modes
  const toggleColorMode = () => {
    setColorMode((prevMode) => {
      const newMode = prevMode === 'light' ? 'dark' : 'light';
      localStorage.setItem('color-mode', newMode);
      return newMode;
    });
  };

  // Apply the color mode to the document
  useEffect(() => {
    const root = window.document.documentElement;
    
    // Remove the opposite class first
    root.classList.remove(isDark ? 'light' : 'dark');
    
    // Add the current theme class
    root.classList.add(colorMode);
    
    // Update the meta theme-color for mobile devices
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute(
        'content',
        isDark ? '#1a202c' : '#ffffff'
      );
    }
    
    // Update body background and text color for elements outside of React
    document.body.style.backgroundColor = isDark ? '#131a29' : '#f8fafc';
    document.body.style.color = isDark ? '#e2e8f0' : '#1a202c';
    
    // Add transition styles to all elements for smooth theme switching
    document.body.style.transition = 'background-color 0.3s ease, color 0.3s ease';
    const addTransitions = () => {
      const css = `
        * {
          transition: background-color 0.3s ease, 
                      border-color 0.3s ease, 
                      color 0.3s ease, 
                      box-shadow 0.3s ease !important;
        }
      `;
      const style = document.createElement('style');
      style.id = 'theme-transitions';
      style.appendChild(document.createTextNode(css));
      document.head.appendChild(style);
      
      // Remove the style after transitions complete to prevent interference with other animations
      setTimeout(() => {
        const transitionStyle = document.getElementById('theme-transitions');
        if (transitionStyle) {
          transitionStyle.remove();
        }
      }, 400);
    };
    
    addTransitions();
  }, [colorMode, isDark]);

  // Listen for system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      // Only update if user hasn't set a preference manually
      if (!localStorage.getItem('color-mode')) {
        setColorMode(e.matches ? 'dark' : 'light');
      }
    };
    
    // Add listener for theme preference changes
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
    }
    
    // Cleanup
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        // Fallback for older browsers
        mediaQuery.removeListener(handleChange);
      }
    };
  }, []);

  return (
    <ColorModeContext.Provider value={{ colorMode, toggleColorMode, isDark }}>
      {children}
    </ColorModeContext.Provider>
  );
};

export const useColorMode = () => useContext(ColorModeContext);

export default ColorModeContext;
